import { useLazyFetchDispatchLegsBySearchQuery } from '@api/Trinium/Endpoints/Dispatch/slice';
import useCurrentTerminal from './useCurrentTerminal';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { useSearchParams } from 'react-router';
import { getObjectChanges } from '@/Infrastructure/Utilities/object.type';
import { getNullableString, getStringOrBlank } from '@/Infrastructure/Utilities/string.type';
import { queueUncontrolledToast } from '@toast';
import { normalizeContainerString } from '@components/DataGrid/Dispatch/ColumnDefinitions/container';
import { useAppDispatch } from '@reduxStore';

/**
 * Custom hook wrapped around the Lazy Fetch Dispatch Legs By Search endpoint to centralize
 * extra logic we need for the Research page.
 */
export default function useResearchQuery() {
	const { number: terminalNumber } = useCurrentTerminal();
	const [searchParams] = useSearchParams();
	const appDispatch = useAppDispatch();

	const [fetchDispatchLegsBySearch, { isLoading, isFetching, isError, error, data }] = useLazyFetchDispatchLegsBySearchQuery();

	/** Identifies the most recent request so a stale response cannot announce missing containers */
	const latestRequestToken = useRef(0);

	const readSearchParameters = useCallback((): ResearchParams => {
		const startRange = getStringOrBlank(searchParams.get('startRange'));
		const endRange = getStringOrBlank(searchParams.get('endRange'));
		const order = getNullableString(searchParams.get('order'));
		const customerId = getStringOrBlank(searchParams.get('customerId'));
		const customerName = getStringOrBlank(searchParams.get('customerName'));
		const createdByUserCode = getStringOrBlank(searchParams.get('createdByUserCode'));
		const container = searchParams.get('container') ? decodeURIComponent(searchParams.get('container') || '').split(',') : null;
		const masterBol = getStringOrBlank(searchParams.get('masterBol'));
		const driver = getStringOrBlank(searchParams.get('driver'));
		const customerRef = getStringOrBlank(searchParams.get('customerRef'));
		const chassis = getStringOrBlank(searchParams.get('chassis'));
		const bookingRef = getStringOrBlank(searchParams.get('bookingRef'));
		const onlyApprovedCharges = parseApprovedChargesParam(
			searchParams.get('isPrimaryChgApproved') ? searchParams.get('isPrimaryChgApproved') : null
		);
		const delvStartRange = getStringOrBlank(searchParams.get('delvStartDate'));
		const delvEndRange = getStringOrBlank(searchParams.get('delvEndDate'));
		const schdPuStartRange = getStringOrBlank(searchParams.get('schdPuStartDate'));
		const schdPuEndRange = getStringOrBlank(searchParams.get('schdPuEndDate'));
		const schdDelvStartRange = getStringOrBlank(searchParams.get('schdDelvStartDate'));
		const schdDelvEndRange = getStringOrBlank(searchParams.get('schdDelvEndDate'));
		const consignorId = getStringOrBlank(searchParams.get('consignorId'));
		const consigneeId = getStringOrBlank(searchParams.get('consigneeId'));

		return {
			startRange,
			endRange,
			order,
			customerId,
			customerName,
			createdByUserCode,
			container,
			masterBol,
			driver,
			customerRef,
			chassis,
			bookingRef,
			onlyApprovedCharges,
			delvStartRange,
			delvEndRange,
			schdPuStartRange,
			schdPuEndRange,
			schdDelvStartRange,
			schdDelvEndRange,
			consignorId,
			consigneeId
		};
	}, [searchParams]);

	/**
	 * Flag used to indicate that the parameters are empty and data should not be returned.
	 * We cannot call the Fetch with no parameters or it will return the Dispatch Board response,
	 * but we also cannot clear/unsubscribe the endpoint easily. When true, endpoint data will
	 * be overridden with an empty array.
	 */
	const [paramsEmpty, setParamsEmpty] = useState(false);
	const [searchValues, setSearchValues] = useState<ResearchParams>(() => {
		return readSearchParameters();
	});

	/**
	 * Callback for executing the fetch request
	 */
	const executeQuery = useCallback(
		(params: ResearchParams) => {
			const searchParams = Object.entries(params).filter(([key, value]) => {
				if (key === 'onlyApprovedCharges') {
					return value !== 'All';
				}
				return Boolean(value);
			}).length;

			if (searchParams > 0) {
				const requestToken = ++latestRequestToken.current;

				const request = fetchDispatchLegsBySearch(
					{
						terminalNumber: terminalNumber,
						jobDocStartDate: params.startRange ?? '',
						jobDocEndDate: params.endRange ?? '',
						jobId: params.order ? Number(params.order) : null,
						custIdStartsWith: getNullableString(params.customerId),
						custNameStartsWith: getNullableString(params.customerName),
						createdByUserCode: getNullableString(params.createdByUserCode),
						cntrIdsStartsWith: params.container ?? null,
						masterBolStartsWith: getNullableString(params.masterBol),
						drvNameStartsWith: getNullableString(params.driver),
						custRefNoStartsWith: getNullableString(params.customerRef),
						chassisIdStartsWith: getNullableString(params.chassis),
						bookingRefNoStartsWith: getNullableString(params.bookingRef),
						isPrimaryChgApproved:
							params.onlyApprovedCharges !== 'All' ? (params.onlyApprovedCharges === 'Only Approved' ? 'yes' : 'no') : null,
						delvStartDate: getNullableString(params.delvStartRange),
						delvEndDate: getNullableString(params.delvEndRange),
						schdPuStartDate: getNullableString(params.schdPuStartRange),
						schdPuEndDate: getNullableString(params.schdPuEndRange),
						schdDelvStartDate: getNullableString(params.schdDelvStartRange),
						schdDelvEndDate: getNullableString(params.schdDelvEndRange),
						consignorIdStartsWith: getNullableString(params.consignorId),
						consigneeIdStartsWith: getNullableString(params.consigneeId)
					},
					false
				);

				const searchedContainers = params.container;
				if (searchedContainers && searchedContainers.length > 0) {
					request
						.unwrap()
						.then((legs) => {
							/** Errors are surfaced by the grid; a superseded request should not announce against stale data */
							if (requestToken !== latestRequestToken.current) {
								return;
							}

							const normalizedLegContainers = legs.map((leg) => normalizeContainerString(leg.cntrId));
							const missingContainers = searchedContainers.filter((searched) => {
								const normalizedSearch = normalizeContainerString(searched);
								if (!normalizedSearch) {
									return false;
								}
								return !normalizedLegContainers.some((legContainer) => legContainer.startsWith(normalizedSearch));
							});

							if (missingContainers.length > 0) {
								/** No autoCloseMs so the toast persists until the user dismisses it */
								appDispatch(
									queueUncontrolledToast({
										type: 'message',
										severity: 'warning',
										message: `No results found for container${missingContainers.length > 1 ? 's' : ''}: ${missingContainers.join(', ')}`
									})
								);
							}
						})
						.catch(() => undefined);
				}

				setParamsEmpty(false);
			} else {
				setParamsEmpty(true);
			}
		},
		[appDispatch, fetchDispatchLegsBySearch, terminalNumber]
	);

	/**
	 * Whenever the local parameter state has changed, execute the query
	 */
	useEffect(
		function processParams() {
			executeQuery(searchValues);
		},
		[searchValues, executeQuery]
	);

	/**
	 * When the Research Search Button is clicked, the search parameters are written.
	 * When the Search Parameters change, determine what changes occurred to parameters we are worried about
	 */
	useEffect(
		function processSearchParameters() {
			const currentState = readSearchParameters();

			/** Only update local state with mutated values, to avoid API call if URL changed without items we care about */
			setSearchValues((state) => {
				const mutations = getObjectChanges(currentState, { ...state });

				if (Object.keys(mutations).length > 0) {
					return {
						...state,
						...mutations
					};
				}

				return state;
			});
		},
		[readSearchParameters]
	);

	return {
		isLoading,
		isFetching,
		isError,
		error,
		data: !paramsEmpty && data ? data : ([] as DispatchLeg[]),
		paramsEmpty
	};
}

interface ResearchParams {
	startRange?: string;
	endRange?: string;
	order: string | null;
	customerId?: string;
	customerName?: string;
	createdByUserCode?: string;
	container: string[] | null;
	masterBol?: string;
	driver?: string;
	customerRef?: string;
	chassis?: string;
	bookingRef?: string;
	onlyApprovedCharges: 'All' | 'Only Unapproved' | 'Only Approved';
	delvStartRange?: string;
	delvEndRange?: string;
	schdPuStartRange?: string;
	schdPuEndRange?: string;
	schdDelvStartRange?: string;
	schdDelvEndRange?: string;
	consignorId?: string;
	consigneeId?: string;
}

function parseApprovedChargesParam(isPrimaryChgApproved: string | null): 'All' | 'Only Unapproved' | 'Only Approved' {
	if (isPrimaryChgApproved === 'yes') {
		return 'Only Approved';
	} else if (isPrimaryChgApproved === 'no') {
		return 'Only Unapproved';
	} else {
		return 'All';
	}
}
